<template>
   <b-container>
      <h2 class="mt-3 mb-4">Import Study Data</h2>

      <b-alert variant="info" :show="!importing && !result && !importErrors">
         <p>
            Upload an Excel workbook containing study data to import. The importer will create a new
            study with all periods, business components, employees, contractors, supplies, and cloud
            computing expenses found in the file.
         </p>
         <p>
            If anything cannot be imported the entire operation is cancelled with no changes made.
         </p>
         <p class="mb-0">
            Your file must follow the
            <a href="/study_import_template.xlsx" download>import template</a>.
         </p>
      </b-alert>

      <!-- File picker -->
      <b-card class="mb-3" v-if="!result">
         <b-form-file
            v-model="file"
            accept=".xlsx,.xls,.xlsm,.xlsb"
            placeholder="Choose an Excel file..."
            browse-text="Browse"
            :disabled="importing"
         />

         <div class="mt-3">
            <b-button
               id="btn-import"
               variant="primary"
               :disabled="!file || importing"
               @click="startImport"
            >
               <b-spinner small v-if="importing" class="mr-1" />
               {{ importing ? 'Importing…' : 'Import Data' }}
            </b-button>
            <b-button
               variant="link"
               :to="{name: 'company-detail', params: {id: companyId}}"
               :disabled="importing"
               class="ml-2"
            >
               Cancel
            </b-button>
         </div>
      </b-card>

      <!-- Import errors -->
      <b-alert variant="danger" :show="importErrors && importErrors.length > 0">
         <h6 class="font-weight-bold mb-2">Import Failed</h6>
         <p>The file could not be imported. Please fix the following issues and try again:</p>
         <ul class="mb-0">
            <li v-for="(err, i) in importErrors" :key="i">{{ err }}</li>
         </ul>
         <div class="mt-3">
            <b-button variant="secondary" size="sm" @click="reset">Try Again</b-button>
         </div>
      </b-alert>

      <!-- Success -->
      <b-alert variant="success" show v-if="result">
         <h6 class="font-weight-bold mb-2">Import Successful</h6>
         <p>
            Study <strong>{{ result.label }}</strong> has been created with the following data:
         </p>
         <ul>
            <li>Periods: {{ result.periods.join(', ') }}</li>
            <li>Business components: {{ result.projects }}</li>
            <li>Activities: {{ result.activities }}</li>
            <li>Employees: {{ result.employees }}</li>
            <li>Contractors: {{ result.contractors }}</li>
            <li>Supplies entries: {{ result.supplies }}</li>
            <li>Cloud computing entries: {{ result.cloudComputing }}</li>
            <li>Gross receipts entries: {{ result.grossReceiptsItems }}</li>
            <li v-if="result.newStates && result.newStates.length > 0">
               States added to credit configuration: {{ result.newStates.join(', ') }}
            </li>
            <li v-if="result.startupYear">Startup year set to: {{ result.startupYear }}</li>
         </ul>

         <b-alert
            variant="warning"
            :show="result.warnings && result.warnings.length > 0"
            class="mb-2"
         >
            <strong>Warnings:</strong>
            <ul class="mb-0 mt-1">
               <li v-for="(w, i) in result.warnings || []" :key="i">{{ w }}</li>
            </ul>
         </b-alert>

         <b-button
            variant="primary"
            size="sm"
            :to="{name: 'study-edit', params: {id: companyId, studyId: result.studyId}}"
         >
            Configure Study Periods
         </b-button>

         <p class="mt-2 mb-0 text-muted small">
            If any of the imported periods were short years or fiscal years, click Configure Study
            Periods to adjust them.
         </p>
      </b-alert>
   </b-container>
</template>

<script>
export default {
   props: ['companyId'],

   data() {
      return {
         file: null,
         importing: false,
         result: null,
         importErrors: null,
      };
   },

   methods: {
      async startImport() {
         this.importing = true;
         this.importErrors = null;
         this.result = null;

         const formData = new FormData();
         formData.append('file', this.file);

         try {
            const response = await this.$http.post(
               `/api/company/${this.companyId}/study/import`,
               formData,
               {headers: {'Content-Type': 'multipart/form-data'}}
            );
            this.result = response.data;
         } catch (err) {
            const status = err.response && err.response.status;
            const data = err.response && err.response.data;
            if (status === 422 && data && data.errors) {
               this.importErrors = Array.isArray(data.errors) ? data.errors : [data.errors];
            } else {
               this.importErrors = ['An unexpected server error occurred. Please try again.'];
            }
         } finally {
            this.importing = false;
         }
      },

      reset() {
         this.file = null;
         this.importErrors = null;
         this.result = null;
      },
   },
};
</script>
